import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import BackButton from '../../components/common/BackButton';

const UNITS = ['kg','quintal','ton','unit'];
const STATUS_STYLE = {
  open:      { bg:'var(--color-success-light)', color:'var(--color-success)',  label:'🟢 Open' },
  accepted:  { bg:'var(--color-info-light)',    color:'var(--color-info)',     label:'✅ Accepted' },
  completed: { bg:'var(--status-growing-bg)',   color:'var(--status-growing-color)', label:'🏁 Done' },
  cancelled: { bg:'var(--gray-100)',            color:'var(--gray-400)',       label:'❌ Cancelled' },
};

export default function MyRequestsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('open');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [saving, setSaving]       = useState(false);

  useEffect(() => { load(); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/requests/my?status=${tab}`);
      setRequests(data.requests || []);
    } catch {} finally { setLoading(false); }
  };

  const startEdit = (req) => {
    setEditingId(req._id);
    setEditForm({
      quantity:          req.quantity,
      quantityUnit:      req.quantityUnit || 'kg',
      offeredPrice:      req.offeredPrice || '',
      offeredPriceUnit:  req.offeredPriceUnit || 'per_kg',
      preferredLocation: req.preferredLocation || '',
      notes:             req.notes || '',
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/requests/${id}`, editForm);
      setRequests(p => p.map(r => r._id === id ? { ...r, ...data.request } : r));
      setEditingId(null);
      toast.success('Request updated ✅');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating request');
    } finally { setSaving(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await api.put(`/requests/${id}/cancel`);
      setRequests(p => p.filter(r => r._id !== id));
      toast.success('Request cancelled');
    } catch { toast.error('Error'); }
  };

  const complete = async (id) => {
    try {
      await api.put(`/requests/${id}/complete`);
      load();
      toast.success('Marked as completed!');
    } catch { toast.error('Error'); }
  };

  const TABS = [
    { id:'open',      label:'🟢 Open' },
    { id:'accepted',  label:'✅ Accepted' },
    { id:'completed', label:'🏁 Done' },
    { id:'cancelled', label:'❌ Cancelled' },
  ];

  return (
    <div className="page">
      <div className="header header--simple">
        <BackButton />
        <div className="header__title" style={{ flex:1 }}>📦 My Requests</div>
        <button className="header__action" onClick={() => navigate('/requests/new')}>+ New</button>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => (
          <button key={t.id} className={`tab${tab===t.id ? ' tab--active':''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>
        {loading ? (
          <div className="full-center"><span className="spinner spinner--lg" /></div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">📦</div>
            <div className="empty-state__title">No {tab} requests</div>
            {tab === 'open' && <button className="btn btn--primary" onClick={() => navigate('/requests/new')}>+ Create Request</button>}
          </div>
        ) : (
          requests.map(req => {
            const st = STATUS_STYLE[req.status] || STATUS_STYLE.open;
            const isEditing = editingId === req._id;
            return (
              <div key={req._id} className="card" style={{ marginBottom:'var(--sp-3)', borderLeft:`4px solid ${st.color}` }}>
                <div className="card__body">
                  {/* Header row */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'var(--sp-2)' }}>
                    <div>
                      <div className="fw-bold text-lg">{req.cropName}</div>
                      <div className="text-sm text-muted">⚖️ {req.quantity} {req.quantityUnit}
                        {req.offeredPrice && <span style={{ color:'var(--color-success)', fontWeight:600, marginLeft:8 }}>₹{req.offeredPrice}/{req.offeredPriceUnit?.replace('per_','')}</span>}
                      </div>
                    </div>
                    <span style={{ background:st.bg, color:st.color, borderRadius:'var(--r-full)', padding:'3px 10px', fontSize:12, fontWeight:500, flexShrink:0 }}>{st.label}</span>
                  </div>

                  {req.preferredLocation && <div className="text-sm text-muted" style={{ marginBottom:'var(--sp-1)' }}>📍 {req.preferredLocation}</div>}
                  {req.notes && <div className="text-sm" style={{ background:'var(--gray-50)', padding:'var(--sp-2) var(--sp-3)', borderRadius:'var(--r-sm)', marginBottom:'var(--sp-2)', fontStyle:'italic', color:'var(--gray-600)' }}>"{req.notes}"</div>}

                  {/* Accepted farmer contact */}
                  {req.status === 'accepted' && req.acceptedBy && (
                    <div style={{ background:'var(--color-success-light)', border:'1.5px solid var(--color-success)', borderRadius:'var(--r-sm)', padding:'var(--sp-3)', marginBottom:'var(--sp-2)' }}>
                      <div className="fw-semi text-sm" style={{ color:'var(--color-success)', marginBottom:4 }}> Farmer Accepted!</div>
                      <div className="fw-semi text-base">{req.acceptedBy.name}</div>
                      <div className="text-sm text-muted"> {req.acceptedBy.locationAddress}</div>
                      <a href={`tel:+91${req.acceptedBy.mobile}`} className="btn btn--primary btn--full" style={{ marginTop:'var(--sp-2)', justifyContent:'center', display:'flex' }}>
                        📞 Call Farmer +91{req.acceptedBy.mobile}
                      </a>
                    </div>
                  )}

                  {/* EDIT FORM */}
                  {isEditing && (
                    <div className="form-group" style={{ background:'var(--gray-50)', padding:'var(--sp-3)', borderRadius:'var(--r-sm)', marginBottom:'var(--sp-2)' }}>
                      <div className="text-sm fw-semi" style={{ marginBottom:'var(--sp-2)' }}>✏️ Edit Request</div>
                      <div className="form-row form-row--2">
                        <div>
                          <label className="label">Quantity</label>
                          <input className="input" type="number" value={editForm.quantity} onChange={e => setEditForm(p=>({...p,quantity:e.target.value}))} />
                        </div>
                        <div>
                          <label className="label">Unit</label>
                          <select className="input" value={editForm.quantityUnit} onChange={e => setEditForm(p=>({...p,quantityUnit:e.target.value}))}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-row form-row--2">
                        <div>
                          <label className="label">Price (₹)</label>
                          <input className="input" type="number" placeholder="Optional" value={editForm.offeredPrice} onChange={e => setEditForm(p=>({...p,offeredPrice:e.target.value}))} />
                        </div>
                        <div>
                          <label className="label">Per</label>
                          <select className="input" value={editForm.offeredPriceUnit} onChange={e => setEditForm(p=>({...p,offeredPriceUnit:e.target.value}))}>
                            <option value="per_kg">kg</option>
                            <option value="per_quintal">quintal</option>
                            <option value="per_unit">unit</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="label">Location</label>
                        <input className="input" placeholder="Preferred location" value={editForm.preferredLocation} onChange={e => setEditForm(p=>({...p,preferredLocation:e.target.value}))} />
                      </div>
                      <div>
                        <label className="label">Notes</label>
                        <textarea className="input" rows={2} value={editForm.notes} onChange={e => setEditForm(p=>({...p,notes:e.target.value}))} />
                      </div>
                      <div className="btn-group">
                        <button className="btn btn--primary btn--full" onClick={() => saveEdit(req._id)} disabled={saving}>
                          {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save'}
                        </button>
                        <button className="btn btn--outline-gray btn--full" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-muted" style={{ marginBottom:'var(--sp-2)' }}>
                    {new Date(req.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                  </div>

                  {/* Actions */}
                  <div className="btn-group">
                    {req.status === 'open' && !isEditing && (
                      <button className="btn btn--outline btn--sm" onClick={() => startEdit(req)}>✏️ Edit</button>
                    )}
                    {req.status === 'open' && (
                      <button className="btn btn--outline-gray btn--sm" onClick={() => cancel(req._id)}>❌ Cancel</button>
                    )}
                    {req.status === 'accepted' && (
                      <button className="btn btn--primary btn--sm" onClick={() => complete(req._id)}>🏁 Mark Complete</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div style={{ height:'var(--sp-4)' }} />
      </div>
    </div>
  );
}
